import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Alert, type AlertProps } from './Alert'
import { cx } from './utils'

function IconInfo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m11.25 11.25.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
      />
    </svg>
  )
}

export type PageInfoBannerProps = Omit<AlertProps, 'tone' | 'children'> & {
  children: ReactNode
}

/** Info banner — τι κάνεις σε αυτή την οθόνη (με εικονίδιο). */
export function PageInfoBanner({ children, className, ...props }: PageInfoBannerProps) {
  const textRef = useRef<HTMLParagraphElement>(null)
  const [singleLine, setSingleLine] = useState(true)

  useLayoutEffect(() => {
    const el = textRef.current
    if (!el) return

    const measure = () => {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20
      setSingleLine(el.scrollHeight <= lineHeight * 1.35)
    }

    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [children])

  return (
    <Alert
      tone="info"
      className={cx('min-w-0 flex-1 py-2.5 pl-3 pr-4', className)}
      {...props}
    >
      <div
        className={cx(
          'flex gap-2.5',
          singleLine ? 'items-center' : 'items-start',
        )}
      >
        <span
          className={cx(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-violet-200/80 text-violet-800 dark:bg-violet-500/25 dark:text-violet-200',
            !singleLine && 'mt-0.5',
          )}
        >
          <IconInfo className="h-4 w-4" />
        </span>
        <p ref={textRef} className="min-w-0 flex-1 text-sm leading-snug">
          {children}
        </p>
      </div>
    </Alert>
  )
}
